import { useState } from "react";
import { cleanedDateString, convertUTCToLocalDate, convertLocalToUTCDate, formattedDateRange, statusBackgroundColor, statusColor, statusTitle, getToday } from "../utils";
import DatePicker from 'react-datepicker';
import '../../../../assets/datePickerCustomStyling.css';
import { useDeleteForecastMutation, useGetCurrentUserQuery, useUpdateForecastMutation, useCreateForecastMutation } from 'utils/wemble-api';
import { ForecastDto } from "utils/wemble-api.generated";
import RemoveButton from '../components/RemoveButton';
import { getStatusSource } from 'utils/getStatusSource';
import { haptic } from 'utils/haptics';
import { setGlobalForecastCooldown } from '../ForecastItem';

// Helper function to get status CSS class name
const getStatusClassName = (status: string | undefined) => {
  switch (status) {
    case '0': return 'status-available';
    case '1': return 'status-some-availability';
    case '2': return 'status-busy';
    case '3': return 'status-seriously-busy';
    case 'away': return 'status-away';
    default: return 'status-available'; // Default to available color
  }
};

const StatusSelect = ({ selectStatus, data, toggle, range, classes }:
  {
    selectStatus: any;
    toggle: any;
    data?: ForecastDto;
    range?: { startDate: string, endDate: string } | any;
    classes: any
  }) => {
  const [updateForecast] = useUpdateForecastMutation();
  const [deleteForecast] = useDeleteForecastMutation();
  const [createForecast] = useCreateForecastMutation();

  const [selected, setSelected] = useState<string | null>(null);
  const [scaled, setScaled] = useState<string | null>(null);
  const [forecastStartDate, setForecastStartDate] = useState<Date | null>(null);
  const [forecastEndDate, setForecastEndDate] = useState<Date | null>(null);


  const { data: user } = useGetCurrentUserQuery();

  const [startDate, setStartDate] = useState<Date | null>(range?.startDate ? convertUTCToLocalDate(new Date(range.startDate)) : null);
  const [endDate, setEndDate] = useState<Date | null>(range?.endDate ? convertUTCToLocalDate(new Date(range.endDate)) : null);

  const onChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      data && updateForecast({ userId: user?._id || '', forecastId: data._id || '', forecastUpsertParams: { startDate: cleanedDateString(convertLocalToUTCDate(start)), endDate: cleanedDateString(convertLocalToUTCDate(end)), status: data.status!, comment: data.comment, sessionId: (window as any)._sessionId, source: getStatusSource() } })
      toggle()
    }
  };

  const createForecastFromToday = (endDate: Date) => {
    if (!user) return;

    const todayDate = getToday();
    const endDateString = cleanedDateString(convertLocalToUTCDate(endDate));
    const currentStatus = user.onLeave ? 'away' : user.currentWorkload?.toString() || '0';

    createForecast({
      userId: user._id || '',
      forecastUpsertParams: {
        startDate: todayDate,
        endDate: endDateString,
        status: currentStatus as "0" | "1" | "2" | "3" | "away",
        comment: null,
        sessionId: (window as any)._sessionId,
        source: getStatusSource()
      }
    });

    toggle();
  };

  const onForecastDateChange = (dates) => {
    const [start, end] = dates;

    // When user clicks a date, we want today as start and clicked date as end
    if (start && !end) {
      const today = new Date();
      setForecastStartDate(today);
      setForecastEndDate(start);

      // Create forecast from today to selected date
      createForecastFromToday(start);

      // Reset state
      setForecastStartDate(null);
      setForecastEndDate(null);
    } else if (start && end) {
      // Handle range selection (shouldn't normally happen in our flow)
      setForecastStartDate(start);
      setForecastEndDate(end);
      createForecastFromToday(end);
      setForecastStartDate(null);
      setForecastEndDate(null);
    }
  };

  // Get the appropriate status for coloring
  const currentStatus = data?.status || (user?.onLeave ? 'away' : user?.currentWorkload?.toString() || '0');
  const statusClassName = getStatusClassName(currentStatus);

  return (
    <>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      }}>

        {(data &&
          <DatePicker
            wrapperClassName="datePickerCustomStyling noOverlap"
            calendarClassName={`${statusClassName} editing-forecast`}
            selectsRange
            selected={startDate}
            startDate={startDate}
            endDate={endDate}
            onChange={onChange}
            calendarStartDay={1}
            formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
            disabledKeyboardNavigation
            monthsShown={('ontouchstart' in window) ? 1 : 2}
            customInput={
              (<div className={classes.suggestionItem} style={{ position: 'relative', height: 24, maxWidth: 100 }}>
                <div className={classes.subtitle}>{formattedDateRange(range)}</div>
              </div>
              )
            }
          />)}

        {!data && range && (<div style={{ width: '100%', textAlign: 'center' }} className={classes.subtitle}>{formattedDateRange(range)}</div>)}

        {!data && !range && (
          <DatePicker
            wrapperClassName="datePickerCustomStyling noOverlap"
            calendarClassName={statusClassName}
            selectsRange
            selected={null}
            startDate={new Date()}
            endDate={forecastEndDate}
            onChange={onForecastDateChange}
            calendarStartDay={1}
            formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
            filterDate={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const compareDate = new Date(date);
              compareDate.setHours(0, 0, 0, 0);
              return compareDate >= today;
            }}
            disabledKeyboardNavigation
            monthsShown={('ontouchstart' in window) ? 1 : 2}
            customInput={
              <div
                className={classes.suggestionItem}
                style={{ position: 'relative', height: 24, maxWidth: 100 }}
              >
                <div className={classes.subtitle}>Add end date</div>
              </div>
            }
          />
        )}

        {data && (
          <RemoveButton
            size={28}
            variant="dark"
            style={{ marginTop: 1, marginBottom: -3 }}
            onClick={() => { haptic('medium'); setGlobalForecastCooldown(); data && deleteForecast({ userId: data.user || '', forecastId: data._id || '', sessionId: (window as any)._sessionId }); toggle(); }}
          />
        )}

      </div>

      {['3', '2', '1', '0', 'away'].map(status => (<div
        key={status}
        className={classes.suggestionItem}
        style={{
          backgroundColor: selected == status ? `${statusColor(status)}60` : scaled == status ? `${statusColor(status)}30` : statusBackgroundColor(status),
          transform: (selected == status || scaled == status) ? 'scale(1.065)' : 'scale(1)',
          cursor: status == data?.status ? 'default' : 'pointer',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
        }}
        onClick={(e) => { e.stopPropagation(); haptic('medium'); selectStatus(status); }}
        onTouchStart={() => setSelected(status)}
        onTouchEnd={() => setSelected(null)}
        {...(!('ontouchstart' in window) ? {
          onMouseEnter: () => setScaled(status),
          onMouseLeave: () => setScaled(null),
          onMouseDown: () => setSelected(status),
          onMouseUp: () => setSelected(null),
        } : {})}
      >
        <div className={classes.title} style={{ color: statusColor(status) }}>
          {statusTitle(status)}
        </div>
      </div>))}

    </>)
}

export default StatusSelect;